import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Image, Video, Trash2, UploadCloud, Link2, Loader2 } from "lucide-react";

function Toggle({ on, onChange, testid }) {
  return (
    <button onClick={() => onChange(!on)} data-testid={testid}
      className={`relative w-12 h-7 rounded-full border-2 border-black transition-colors ${on ? "bg-[#86EFAC]" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white border-2 border-black rounded-full transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

export default function SiteSettings() {
  const [cfg, setCfg] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkType, setLinkType] = useState("image");
  const [linkTitle, setLinkTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/site-settings").then(r => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setToggle = async (key, val) => {
    setCfg(c => ({ ...c, [key]: val }));
    try { await api.put("/global-admin/site-settings", { [key]: val }); toast.success("Saved"); }
    catch { toast.error("Save failed"); load(); }
  };

  const addLink = async () => {
    if (!linkUrl.trim()) { toast.info("Paste a media URL (YouTube/Bunny/image)"); return; }
    try {
      await api.post("/global-admin/site-hero/media", { url: linkUrl.trim(), type: linkType, source: "link", title: linkTitle });
      setLinkUrl(""); setLinkTitle(""); toast.success("Media link added"); load();
    } catch { toast.error("Failed"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("title", file.name);
    try {
      await api.post("/global-admin/site-hero/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to Azure"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const delMedia = async (id) => { await api.delete(`/global-admin/site-hero/media/${id}`); load(); };

  if (!cfg) return <div className="mono text-sm">Loading site settings…</div>;

  return (
    <div className="space-y-6" data-testid="site-settings">
      <div className="flex items-center gap-2"><Globe size={20} /><h2 className="display text-xl font-bold">Site Settings</h2></div>

      <div className="nb-card p-5 bg-white border-2 border-black space-y-4">
        <h3 className="font-bold">Standalone Landing Pages</h3>
        <div className="flex items-center justify-between">
          <div><div className="font-bold text-sm">Enable Standalone Store Landing</div>
            <div className="text-xs text-slate-500">When OFF, /store redirects to /#storez</div></div>
          <Toggle on={cfg.enable_store_landing} onChange={v => setToggle("enable_store_landing", v)} testid="toggle-store-landing" />
        </div>
        <div className="flex items-center justify-between">
          <div><div className="font-bold text-sm">Enable Standalone TV Landing</div>
            <div className="text-xs text-slate-500">When OFF, /tv redirects to /#ponderr-tv</div></div>
          <Toggle on={cfg.enable_tv_landing} onChange={v => setToggle("enable_tv_landing", v)} testid="toggle-tv-landing" />
        </div>
      </div>

      <div className="nb-card p-5 bg-white border-2 border-black space-y-4" data-testid="site-hero">
        <div className="flex items-center gap-2"><Image size={18} /><h3 className="font-bold">Hero Media Gallery</h3>
          <span className={`nb-badge text-[10px] ${cfg.azure_configured ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}>
            {cfg.azure_configured ? "Azure ready" : "Azure not configured"}
          </span></div>

        {/* Upload (Azure) */}
        <div>
          <div className="text-xs font-bold uppercase text-slate-500 mb-1">Upload image (1920×1080) or MP4 → Azure ({cfg.azure_configured ? "ponderrcont" : "disabled"})</div>
          <label className={`nb-btn text-sm inline-flex ${cfg.azure_configured ? "" : "opacity-50 cursor-not-allowed"}`} data-testid="hero-upload-label">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload to Azure
            <input type="file" accept="image/*,video/mp4" className="hidden" disabled={!cfg.azure_configured || uploading}
              onChange={e => upload(e.target.files[0])} data-testid="hero-upload-input" />
          </label>
          {!cfg.azure_configured && <p className="text-xs text-amber-700 mt-1">Add AZURE_STORAGE_ACCOUNT + AZURE_STORAGE_ACCOUNT_KEY to enable uploads. Use the link option below meanwhile.</p>}
        </div>

        {/* Add link (YouTube/Bunny/URL) */}
        <div className="border-t-2 border-black pt-3">
          <div className="text-xs font-bold uppercase text-slate-500 mb-1 flex items-center gap-1"><Link2 size={12} /> Add media by link (YouTube / Bunny / image URL)</div>
          <div className="grid sm:grid-cols-4 gap-2">
            <select className="nb-select text-sm" value={linkType} onChange={e => setLinkType(e.target.value)} data-testid="hero-link-type">
              <option value="image">Image</option><option value="video">Video</option>
            </select>
            <input className="nb-input text-sm sm:col-span-2" placeholder="https://…" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} data-testid="hero-link-url" />
            <button className="nb-btn text-sm" onClick={addLink} data-testid="hero-link-add">Add</button>
          </div>
          <input className="nb-input text-sm mt-2" placeholder="Title (optional)" value={linkTitle} onChange={e => setLinkTitle(e.target.value)} data-testid="hero-link-title" />
        </div>

        {/* Existing media */}
        <div className="grid sm:grid-cols-3 gap-3" data-testid="hero-media-list">
          {(cfg.hero_media || []).map(m => (
            <div key={m.id} className="nb-card-sm p-2 border-2 border-black" data-testid={`hero-media-${m.id}`}>
              <div className="h-24 bg-slate-100 border border-black rounded overflow-hidden grid place-items-center mb-1">
                {m.type === "video" ? <Video size={26} className="opacity-50" /> : <img src={m.url} alt={m.title} className="w-full h-full object-cover" />}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] truncate">{m.source} · {m.type}</span>
                <button onClick={() => delMedia(m.id)} data-testid={`hero-media-del-${m.id}`}><Trash2 size={13} className="text-red-600" /></button>
              </div>
            </div>
          ))}
          {(cfg.hero_media || []).length === 0 && <div className="text-sm text-slate-500 col-span-full">No hero media yet — upload or add a link.</div>}
        </div>
      </div>
    </div>
  );
}
